<script setup lang="ts">
import { PostType, AuthView, type GridItem } from "#imports";

import { useRouteQuery } from "@vueuse/router";
import { usePageSlider } from "~/composables/usePageSlider";
import { AppDialog } from "#components";
import seedrandom from "seedrandom";

const { getPostLink } = usePosts();
const config = useRuntimeConfig();

const { isLoggedIn } = useAuth();

false && /*#__PURE__*/ defineI18nRoute({ paths: { pl: "/[type]" } });
definePageMeta({ footerContained: true });

const route = useRoute();
const router = useRouter();

const localePath = useLocalePath();

const page = useRouteQuery<number>("page", 1, {
  route,
  router,
  transform: Number,
  mode: "push",
});

// -----------------------------
// Post type

const typeSlug = computed(() => route.params.type as string);

const { getPostTypeFromSlug, getPostTypeTitleSeo, getPostTypeIconComponent } =
  usePostTypes();

const postType = computed(() => getPostTypeFromSlug(typeSlug));

if (!isDefined(typeSlug) || !isDefined(postType)) {
  await navigateTo("/", { replace: true, redirectCode: 301 });
  // throw createError({
  //   statusCode: 404,
  //   statusMessage: "Page not found",
  //   unhandled: false,
  //   message: "PAGE_NOT_FOUND",
  // });
}

// -----------------------------
// SEO

const { getPostTypeTitlePlural } = usePostTypes();

const title = computed(
  () =>
    getPostTypeTitleSeo(postType.value!) +
    (page.value > 1 ? ` (strona ${page.value})` : "")
);

const { titleTemplate } = useSeo();
const ogTitle = computed(() => titleTemplate(title.value || undefined));

const description = computed(() => {
  return `Szeroki wybór na kategorii ${getPostTypeTitlePlural(
    postType.value!
  )}. Wejdź i sprawdź na nowoczesnastodola.pl`;
});

useSeoMeta({
  title,
  ogTitle,
  description,
  ogDescription: description,
  twitterDescription: description,
});

// -----------------------------
// Random sorting

const useRandomSorting = computed(() =>
  [
    PostType.Architect,
    PostType.Contractor,
    PostType.Professional,
    PostType.Project,
    PostType.Relaxation,
  ].includes(postType.value!)
);

const useStrategicFirst = computed(() =>
  [PostType.Professional, PostType.Architect].includes(postType.value!)
);

const useStaticPositions = computed(() => [PostType.Architect].includes(postType.value!))

// -----------------------------
// Posts

useHead({
  link: () => [
    {
      rel: "canonical",
      href:
        config.public.baseUrl +
        localePath({
          name: "type",
          params: { type: typeSlug.value },
          query: { page: page.value > 1 ? page.value : undefined },
        }),
    },
  ],
});

// default limit is 20, but -1 for random sorting
const limit = computed(() => (useRandomSorting.value ? -1 : 20));

const { filter } = usePostFilter();

const { defaultPosts } = usePosts();
const { loadPosts } = usePostListing();

// Poland bounds
// 14.0745211117, 49.0273953314, 24.0299857927, 54.8515359564
const defaultBounds = {
  north: 54.8515359564,
  south: 49.0273953314,
  west: 14.0745211117,
  east: 24.0299857927,
};

const bounds = ref<google.maps.LatLngBoundsLiteral>(defaultBounds);

const mounted = ref(false);

onMounted(() => {
  if (!mounted.value) {
    mounted.value = true;
  }
});

const {
  data: postsData,
  status: postsLoadingStatus,
  execute,
} = await useAsyncData(
  `posts:${postType.value}:index`,
  async () =>
    await loadPosts({
      type: postType,
      page,
      limit,
      filter,
      bounds: mounted.value && isMapOpen.value ? bounds : undefined,
    }),
  { immediate: true, default: () => defaultPosts }
);

watch([page, limit, filter, bounds], () => execute());

// -----------------------------
// Pagination

const total = computed(() => Number(postsData.value?.meta?.filter_count || 0));

// -----------------------------
// Random posts

const { data: seed } = await useAsyncData(
  `${postType.value}-seed`,
  async () => Math.random().toString(),
  { default: () => Math.random().toString() }
);

const randomPosts = computed<PostItem[] | undefined>(() => {
  if (!useRandomSorting.value) return undefined;

  const posts = postsData.value.data;

  const random = seedrandom(seed.value, { entropy: false, global: false });
  const shuffled = posts.slice().sort(() => random() - 0.5);

  let result: PostItem[] = [];

  if (useStrategicFirst.value) {
    // move strategic posts to the beginning, keep everything shuffled
    const strategic = shuffled.filter((post) => post.strategic);
    const rest = shuffled.filter((post) => !post.strategic);

    result = [...strategic, ...rest];
  } else {
    result = shuffled;
  }

  if (useStaticPositions.value) {
    // move posts with static positions set (position property) to defined positions (value of position property), keep everything shuffled, do not override existing positions
    const staticPosts = result.filter((post) => post.position) as (PostItem & { position: number })[];
    result = result.filter((post) => !post.position);

    staticPosts.forEach((post) => {
      const index = post.position - 1;
      result.splice(index, 0, post);
    });
  }

  return result;
});

// -----------------------------
// Grid items

const { postToGrid, splitItems } = useGrid();

const items = computed(() =>
  postsLoadingStatus.value === "pending"
    ? Array.from<"loading">({ length: limit.value || 20 }).fill("loading")
    : randomPosts.value?.map((item, index) =>
      postToGrid(item, undefined, { lazyIndex: 2, index })
    ) ||
    postsData.value?.data?.map((item, index) =>
      postToGrid(item, undefined, { lazyIndex: 2, index })
    ) ||
    []
);

const { isMobile } = useUserDevice();
const splitIndex = ref(8);

const updateSplitIndex = (isMobile: boolean) => {
  splitIndex.value = isMobile ? 5 : 8;
};

onMounted(() => updateSplitIndex(isMobile.value));
watch(isMobile, (isMobile) => updateSplitIndex(isMobile));

const { itemsBefore, itemsAfter } = splitItems<"loading" | GridItem>(
  items,
  splitIndex
);

// -----------------------------
// Breadcrumbs

const breadcrumbs = useBreadcrumbItems();

// -----------------------------
// Slider

const { getSlides } = usePageSlider();

const { data: slider, status: sliderStatus } = await useAsyncData(
  `slides:${postType.value}`,
  async () => await getSlides(postType.value),
  { immediate: true }
);

const sliderItems = computed(() => slider.value || []);

// -----------------------------
// Schema.org

useSchemaOrg([
  defineItemList({
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: items.value.reduce<any[]>((acc, item) => {
      if (item !== "loading" && item.post?.type && item.post?.slug) {
        const listItem = defineListItem({
          "@type": "ListItem",
          position: acc.length + 1,
          url:
            config.public.baseUrl + getPostLink(item.post.type, item.post.slug),
        });

        acc.push(listItem);
      }

      return acc;
    }, []),
  }),
]);

const { currentPalette } = useTheme();

// -----------------------------
// Footer

const { getFooterContent } = useFooter();

const { data: footerContent } = await useAsyncData(
  `footer:${postType.value}`,
  async () => await getFooterContent(postType.value!),
  { immediate: true, watch: [postType] }
);

// -----------------------------
// CTA

const { getCtaContent } = useCta();

const { data: ctaContent } = await useAsyncData(
  `cta:${postType.value}`,
  async () => await getCtaContent(postType.value!),
  { immediate: true, watch: [postType] }
);

const showCta = computed(
  () =>
    !isLoggedIn.value &&
    !!ctaContent.value &&
    items.value.length > (isMobile.value ? 5 : 8)
);

// -----------------------------
// Map

const mapDialogOpen = ref(false);

const hasMap = computed(() =>
  [PostType.Relaxation, PostType.Architect, PostType.Contractor].includes(
    postType.value!
  )
);

const isMapOpen = ref(
  !isMobile.value && postType.value === PostType.Relaxation
);

const mayMapBeOpen = computed(() => hasMap.value && isMapOpen.value);

const hoveringPost = ref<PostItem>();

const navigateToPost = (event: MouseEvent, post?: PostItem) => {
  event.preventDefault();

  if (post) {
    router.push(getPostLink(post.type!, post.slug!));
  }
};

const markers = computed(() => {
  return items.value
    ?.filter((item) => item !== "loading")
    ?.filter((item) => item.post?.location_map_marker)
    ?.map((item) => ({
      id: item.post?.id,
      bounce:
        hoveringPost && hoveringPost.value?.id === item.post?.id ? true : false,
      data: item.post,
      marker: {
        position: {
          lat: (item.post!.location_map_marker?.coordinates[1] as number) || 0,
          lng: (item.post!.location_map_marker?.coordinates[0] as number) || 0,
        },
      },
    }));
});

// -----------------------------
// Auth

const { openAuthDialog } = useAuth();

// -----------------------------
// Loading

const isLoading = computed(() => postsLoadingStatus.value === "pending");

const resetPosts = () => {
  page.value = 1;
};

watch([bounds, isMapOpen, filter], () => resetPosts());

watch(isMapOpen, () => nextTick(execute));

watch(bounds, (bounds, oldBounds) => {
  if (
    bounds["east"] !== oldBounds["east"] ||
    bounds["west"] !== oldBounds["west"] ||
    bounds["north"] !== oldBounds["north"] ||
    bounds["south"] !== oldBounds["south"]
  ) {
    nextTick(execute);
  }
});

watchImmediate([page], () => {
  if (import.meta.browser) {
    window.scrollTo({ top: 0, behavior: "instant" });
  }
});
</script>

<template>
  <PageBreadcrumbs v-bind="{ breadcrumbs }" />

  <div :class="['page', `page--${postType}`]">
    <div class="page__wrapper grow">
      <template v-if="!isLoggedIn">
        <div v-if="sliderStatus === 'pending'" class="mb-4 lg:mb-5">
          <div class="container">
            <AppSkeleton block class="w-full !pt-[67.988668555%] lg:!pt-[34.264705882%] animate-pulse" />
          </div>
        </div>
        <LazyPageSlider v-else-if="sliderItems.length > 0" :items="sliderItems" />
      </template>

      <PageHeader>
        <template #start>
          <PostFilter :categories="postType ? [postType] : []" />

          <PostGridMapSwitch v-if="hasMap" @update:model-value="(event) => (isMapOpen = event === 'map')"
            :model-value="isMapOpen ? 'map' : 'grid'" />
        </template>

        <template #end>
          <PageTitle :default="postType ? $t(`post.type.${postType}.listing.title.default`) : ''
            " :append="postType ? $t(`post.type.${postType}.listing.title.append`) : ''
              " :mobile="postType ? $t(`post.type.${postType}.listing.title.mobile`) : ''
                " class="text-end">
            <template v-if="page > 1" #additional>
              <span class="sr-only"> (strona {{ page }}) </span>
            </template>
          </PageTitle>
        </template>
      </PageHeader>

      <div class="grid lg:mt-2.5 gap-10 lg:container" :class="{ 'lg:grid-cols-2': mayMapBeOpen }">
        <div v-if="!isMobile || !mayMapBeOpen" class="relative min-w-0 max-w-full">
          <PostGrid v-if="!mounted" id="post-grid-loading" :key="`post-grid-loading-${splitIndex}-${seed}`" :items="Array.from({ length: limit > 0 ? limit : 20 }).map(
            () => 'loading'
          )
            " class="mb-10" mobile-container :loading="isLoading" :with-map="isMapOpen" />

          <ClientOnly>
            <PostGrid id="post-grid-before" :key="`post-grid-before-${splitIndex}-${seed}`"
              :items="showCta ? itemsBefore : items" class="mb-10" mobile-container :loading="isLoading"
              @mouseenter="hoveringPost = $event" @mouseleave="hoveringPost = undefined" :with-map="isMapOpen" />

            <PostGridCta v-if="showCta" id="post-grid-cta" key="post-grid-cta" :pre-title="ctaContent!.pre_title"
              :title="{ append: ctaContent!.title }" :content="ctaContent!.content?.split('\n\n') || []" :cta="{
                prompt: ctaContent!.action_prompt,
                button: ctaContent!.action_button,
              }" @click:action="openAuthDialog(AuthView.Register)" />

            <PostGrid v-if="showCta" id="post-grid-after" :key="`post-grid-after-${splitIndex}-${seed}`"
              :items="itemsAfter" mobile-container class="mb-10" :loading="isLoading"
              @mouseenter="hoveringPost = $event" @mouseleave="hoveringPost = undefined" :with-map="isMapOpen" />
          </ClientOnly>

          <AppPagination v-if="limit > 0" v-model="page" :items-per-page="limit" :total-items="total"
            :max-pages-shown="3" :show-ending-buttons="false" hide-prev-next-when-ends :link-url="localePath({
              query: { ...route.query, page: '[page]' },
            })
              " class="my-10" container />
        </div>

        <!-- <KeepAlive> -->
        <div v-show="hasMap && mayMapBeOpen" class="relative pb-10">
          <div
            class="sticky top-[5.5rem] w-full h-[40rem] lg:h-[min(47.875rem,_calc(100vh_-_5.5rem_-_2.5rem))] bg-grey-100">
            <component :is="mapDialogOpen ? AppDialog : 'div'" :is-open="mapDialogOpen ? true : undefined"
              :big="mapDialogOpen ? true : undefined" @update:is-open="mapDialogOpen = $event">
              <AppMap class="absolute inset-0 block !w-full !h-full" :class="{
                'border-[0.0625rem] border-[#F1F1F1]': !mapDialogOpen,
              }" v-model:bounds="bounds" :default-bounds="defaultBounds" :clusterer="{ enabled: true }" :options="{
                center: { lat: 51.9189046, lng: 19.1343786 },
                mapId: 'a3a2ac208fdd4128',
                zoom: 6,
                clickableIcons: false,
              }" :markers="markers" window show-zoom :show-fullscreen="!mapDialogOpen" :is-fullscreen="mapDialogOpen"
                @open-fullscreen="mapDialogOpen = true" @close-fullscreen="mapDialogOpen = false">
                <template #marker="{ data, id }">
                  <AppMapMarker :id="id" size="sm" tag="button" :bg-class="currentPalette.bg" :title="data?.title">
                    <component :is="postType ? getPostTypeIconComponent(postType) : 'div'
                      " />
                  </AppMapMarker>
                </template>

                <template #marker-clusterer="{ item }">
                  <AppMapMarker size="md" tag="button" :count="item.count" :bg-class="currentPalette.bg">
                    <component :is="postType ? getPostTypeIconComponent(postType) : 'div'
                      " />
                  </AppMapMarker>
                </template>

                <template #window="{ data }">
                  <div v-if="data" class="flex flex-col items-stretch gap-2">
                    <div v-if="data.cover?.file" class="rounded-tl-2xl overflow-hidden">
                      <a :href="getPostLink(
                        data.type || PostType.Inspiration,
                        data.slug || ''
                      )
                        " @click.stop="navigateToPost($event, data)">
                        <NuxtPicture provider="directus" :src="data.cover.file.id +
                          (data.cover.file.filename_download
                            ? '/' +
                            encodeURIComponent(
                              data.cover.file.filename_download
                            )
                            : '')
                          " :class="['block w-full h-auto', currentPalette.bg]" :width="data.cover.file.width"
                          :height="data.cover.file.height" :alt="data.cover.file.description ||
                            data.cover.caption ||
                            data.title
                            " sizes="55vw xs:213px sm:213px md:213px lg:213px xl:213px xxl:213px" :img-attrs="{
                              class: 'block w-full h-auto',
                            }" quality="65" preload />
                      </a>
                    </div>

                    <div class="flex flex-col items-stretch gap-[0.3125rem]">
                      <h2 class="text-xs leading-[1.125rem] font-medium font-body">
                        <a :href="getPostLink(
                          data.type || PostType.Inspiration,
                          data.slug || ''
                        )
                          " @click.stop="navigateToPost($event, data)">
                          {{ data.title }}
                        </a>
                      </h2>
                      <p v-if="data.subtitle" class="text-xs leading-[0.875rem] font-body">
                        <span>
                          {{ data.subtitle }}
                        </span>
                      </p>
                    </div>
                  </div>
                </template>
              </AppMap>
            </component>
          </div>
        </div>
        <!-- </KeepAlive> -->
      </div>
    </div>

    <AppFooterBefore v-if="footerContent" :pre-title="footerContent.pre_title" :title="{
      default: footerContent.title_normal,
      append: footerContent.title_decorative,
    }" :content="footerContent.content?.split('\n\n') || []" />
  </div>
</template>
